import {
	AccountBalanceWallet as AccountBalanceWalletIcon,
	Description as DescriptionIcon,
	DirectionsCar as DirectionsCarIcon,
	Speed as SpeedIcon,
	Download as DownloadIcon
} from "@mui/icons-material";
import { Avatar, Box, Grid2, Skeleton, Tooltip, Typography } from "@mui/material";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import ReactECharts from "echarts-for-react";
import moment from "moment";
import { FC, useMemo, useRef, useState } from "react";

import { useAppSelector } from "../../redux";
import {
	useGetChallanComprehensiveStatsQuery,
	useGetChallanMetricsQuery,
	useGetChallanStatsQuery,
	useGetInterceptorChallanStatsQuery
} from "../../redux/reducers/dashboard.reducer";
import { formatNumberMetric } from "../../utils/commonUtils";
import { TIME_FILTER_FORMAT } from "../../utils/constants";
import { LoadingButton } from "@mui/lab";

const ChallanReportsTab: FC = () => {
	const filtersState = useAppSelector((state) => state.filters);
	const pdfRef = useRef<HTMLDivElement>(null);
	const pdfPrintRef = useRef<HTMLDivElement>(null);
	const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
	const violationChartRef = useRef<any>(null);
	const interceptorChartRef = useRef<any>(null);
	// const jurisdictionChartRef = useRef<any>(null);
	const challanStatsChartRef = useRef<any>(null);
	const pdfViolationChartRef = useRef<any>(null);
	const pdfInterceptorChartRef = useRef<any>(null);
	const pdfJurisdictionChartRef = useRef<any>(null);
	const pdfChallanStatsChartRef = useRef<any>(null);

	const handleDownloadPDF = async () => {
		if (!pdfPrintRef.current) return;

		setIsGeneratingPDF(true);

		try {
			// Force resize of all PDF charts
			const pdfChartRefs = [
				pdfViolationChartRef,
				pdfInterceptorChartRef,
				pdfJurisdictionChartRef,
				pdfChallanStatsChartRef
			];

			for (const chartRef of pdfChartRefs) {
				if (chartRef.current) {
					const echartsInstance = chartRef.current.getEchartsInstance();
					if (echartsInstance && typeof echartsInstance.resize === "function") {
						echartsInstance.resize();
					}
				}
			}

			// Generate canvas from the hidden PDF div with higher quality settings
			const canvas = await html2canvas(pdfPrintRef.current, {
				scale: 4,
				useCORS: true,
				backgroundColor: "#ffffff",
				logging: false,
				allowTaint: true,
				windowWidth: 1200,
				windowHeight: window.innerHeight
			});

			// Create PDF with higher quality image
			const imgData = canvas.toDataURL("image/png", 1.0);
			const pdf = new jsPDF("p", "mm", "a4");
			const pdfWidth = pdf.internal.pageSize.getWidth();
			const pdfHeight = pdf.internal.pageSize.getHeight();
			const imgWidth = canvas.width;
			const imgHeight = canvas.height;
			const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
			const imgX = (pdfWidth - imgWidth * ratio) / 2;
			let imgY = 10;

			const totalHeight = imgHeight * ratio;
			let heightLeft = totalHeight;

			pdf.addImage(imgData, "PNG", imgX, imgY, imgWidth * ratio, imgHeight * ratio, undefined, "FAST");
			heightLeft -= pdfHeight - 20;

			while (heightLeft > 0) {
				pdf.addPage();
				imgY = -(totalHeight - heightLeft - 5);
				pdf.addImage(imgData, "PNG", imgX, imgY, imgWidth * ratio, imgHeight * ratio, undefined, "FAST");
				heightLeft -= pdfHeight - 20;
			}

			const fileName = `Challan_Reports_${moment().format("YYYY-MM-DD_HH-mm-ss")}.pdf`;
			pdf.save(fileName);
		} catch (error) {
			console.error("Error generating PDF:", error);
		} finally {
			setIsGeneratingPDF(false);
		}
	};

	// GET JURISDICTION VIOLATION STATISTICS
	// const {
	// 	data: jurisdictionViolationStatisticsData,
	// 	isLoading: jurisdictionViolationStatisticsLoading,
	// 	isFetching: jurisdictionViolationStatisticsFetching
	// } = useGetJurisdictionViolationStatisticsQuery({
	// 	start_date: moment(filtersState.timeFilter.start_time, TIME_FILTER_FORMAT).format("YYYY-MM-DD"),
	// 	end_date: moment(filtersState.timeFilter.end_time, TIME_FILTER_FORMAT).format("YYYY-MM-DD")
	// });

	// GET CHALLAN STATS
	const {
		data: challanStats,
		isLoading: getChallanStatsLoading,
		isFetching: getChallanStatsFetching
	} = useGetChallanStatsQuery({
		start_date: moment(filtersState.timeFilter.start_time, TIME_FILTER_FORMAT).format("YYYY-MM-DD"),
		end_date: moment(filtersState.timeFilter.end_time, TIME_FILTER_FORMAT).format("YYYY-MM-DD")
	});

	// GET INTERCEPTOR CHALLAN STATS
	const {
		data: interceptorChallanStats,
		isLoading: interceptorChallanStatsLoading,
		isFetching: interceptorChallanStatsFetching
	} = useGetInterceptorChallanStatsQuery({
		start_date: moment(filtersState.timeFilter.start_time, TIME_FILTER_FORMAT).format("YYYY-MM-DD"),
		end_date: moment(filtersState.timeFilter.end_time, TIME_FILTER_FORMAT).format("YYYY-MM-DD")
	});

	// GET CHALLAN METRICS
	const {
		data: challanMetrics,
		isLoading: challanMetricsLoading,
		isFetching: challanMetricsFetching
	} = useGetChallanMetricsQuery({
		start_date: moment(filtersState.timeFilter.start_time, TIME_FILTER_FORMAT).format("YYYY-MM-DD"),
		end_date: moment(filtersState.timeFilter.end_time, TIME_FILTER_FORMAT).format("YYYY-MM-DD")
	});

	// GET CHALLAN COMPREHENSIVE STATS
	const {
		data: challanComprehensiveStats,
		isLoading: challanComprehensiveStatsLoading,
		isFetching: challanComprehensiveStatsFetching
	} = useGetChallanComprehensiveStatsQuery({
		start_date: moment(filtersState.timeFilter.start_time, TIME_FILTER_FORMAT).format("YYYY-MM-DD"),
		end_date: moment(filtersState.timeFilter.end_time, TIME_FILTER_FORMAT).format("YYYY-MM-DD")
	});

	// E-Challan Status Overview Line Chart
	/* const eChallanStatusOptions = useMemo(
		() => ({
			color: ["#43A047", "#FFC000", "#E53935"],
			tooltip: {
				trigger: "axis",
				confine: true
			},
			legend: {
				data: ["Paid", "Pending", "Unpaid"],
				bottom: 0,
				textStyle: {
					fontSize: 12
				}
			},
			grid: { left: 0, right: 25, bottom: 40, containLabel: true },
			xAxis: {
				type: "category",
				data: ["12:00 AM", "02:00 AM", "04:00 AM", "06:00 AM", "08:00 AM", "10:00 AM", "12:00 PM", "02:00 PM"],
				boundaryGap: false,
				axisLine: { show: false }
			},
			yAxis: {
				type: "value",
				axisLine: { show: false },
				splitLine: { lineStyle: { color: "#f0f0f0" } }
			},
			series: [
				{
					name: "Paid",
					data: [2, 4, 3, 2, 3, 2, 4, 3],
					type: "line",
					smooth: false,
					symbol: "circle",
					symbolSize: 6
				},
				{
					name: "Pending",
					data: [3, 5, 4, 3, 4, 5, 3, 2],
					type: "line",
					smooth: false,
					symbol: "circle",
					symbolSize: 6
				},
				{
					name: "Unpaid",
					data: [4, 3, 6, 4, 3, 4, 6, 4],
					type: "line",
					smooth: false,
					symbol: "circle",
					symbolSize: 6
				}
			]
		}),
		[]
	); */

	// Aging Analysis Donut Chart
	/* const agingAnalysisOptions = useMemo(
		() => ({
			color: ["#FF9800", "#43A047", "#1976D2", "#9C27B0"],
			tooltip: {
				trigger: "item",
				formatter: "{b}: {c} ({d}%)"
			},
			legend: {
				show: false
			},
			series: [
				{
					type: "pie",
					radius: ["60%", "90%"],
					avoidLabelOverlap: false,
					label: { show: false },
					data: [
						{ value: 65, name: "0-30 days" },
						{ value: 20, name: "31-60 days" },
						{ value: 10, name: "61-90 days" },
						{ value: 5, name: "90+ days" }
					]
				}
			]
		}),
		[]
	); */

	// Challan Revenue Breakdown by Violation Stacked Bar Chart
	/* const revenueBreakdownOptions = useMemo(
		() => ({
			color: ["#1976D2", "#2196F3", "#03A9F4", "#00BCD4", "#009688", "#4CAF50", "#8BC34A"],
			tooltip: {
				trigger: "axis",
				axisPointer: {
					type: "shadow"
				}
			},
			legend: {
				data: [
					"Overspeeding",
					"No seatbelt",
					"Triple Seat Riding",
					"No helmet",
					"Wrong Direction",
					"Signal Jump",
					"Opposite Side Direction"
				],
				bottom: 0,
				textStyle: {
					fontSize: 11
				}
			},
			grid: { left: 0, right: 25, bottom: 80, containLabel: true },
			xAxis: {
				type: "category",
				data: [
					"12:00 AM",
					"02:00 AM",
					"04:00 AM",
					"06:00 AM",
					"08:00 AM",
					"10:00 AM",
					"12:00 PM",
					"02:00 PM",
					"04:00 PM",
					"06:00 PM",
					"08:00 PM",
					"10:00 PM"
				],
				axisLine: { show: false }
			},
			yAxis: {
				type: "value",
				axisLine: { show: false },
				splitLine: { lineStyle: { color: "#f0f0f0" } }
			},
			series: [
				{
					name: "Overspeeding",
					type: "bar",
					stack: "total",
					data: [50, 60, 70, 80, 90, 100, 110, 120, 130, 140, 150, 160]
				},
				{
					name: "No seatbelt",
					type: "bar",
					stack: "total",
					data: [30, 35, 40, 45, 50, 55, 60, 65, 70, 75, 80, 85]
				},
				{
					name: "Triple Seat Riding",
					type: "bar",
					stack: "total",
					data: [20, 25, 30, 35, 40, 45, 50, 55, 60, 65, 70, 75]
				},
				{
					name: "No helmet",
					type: "bar",
					stack: "total",
					data: [40, 45, 50, 55, 60, 65, 70, 75, 80, 85, 90, 95]
				},
				{
					name: "Wrong Direction",
					type: "bar",
					stack: "total",
					data: [25, 30, 35, 40, 45, 50, 55, 60, 65, 70, 75, 80]
				},
				{
					name: "Signal Jump",
					type: "bar",
					stack: "total",
					data: [35, 40, 45, 50, 55, 60, 65, 70, 75, 80, 85, 90]
				},
				{
					name: "Opposite Side Direction",
					type: "bar",
					stack: "total",
					data: [15, 20, 25, 30, 35, 40, 45, 50, 55, 60, 65, 70]
				}
			]
		}),
		[]
	); */

	// Violation Type Statistics Bar Chart (using Challan Stats - Offences)
	const violationTypeStatisticsOptions = useMemo(() => {
		if (!challanStats?.jurisdiction_statistics?.length) {
			return {
				color: [
					"#1976D2",
					"#2196F3",
					"#03A9F4",
					"#00BCD4",
					"#009688",
					"#4CAF50",
					"#8BC34A",
					"#FF9800",
					"#E91E63",
					"#9C27B0"
				],
				tooltip: { trigger: "axis" },
				grid: { left: 0, right: 25, bottom: 0, containLabel: true },
				xAxis: { type: "category", data: [], axisLine: { show: false } },
				yAxis: { type: "value", axisLine: { show: false }, splitLine: { lineStyle: { color: "#f0f0f0" } } },
				series: []
			};
		}

		// Aggregate offence data from all jurisdictions
		const offenceMap = new Map<string, number>();
		challanStats.jurisdiction_statistics.forEach((jurisdiction) => {
			jurisdiction.offence_breakdown?.forEach((offence) => {
				const currentCount = offenceMap.get(offence.offence_name) || 0;
				offenceMap.set(offence.offence_name, currentCount + offence.challan_count);
			});
		});

		// Convert map to array and sort by count (descending)
		const offenceArray = Array.from(offenceMap.entries())
			.map(([offence_name, challan_count]) => ({
				offence_name,
				challan_count,
				formattedName: offence_name
					.replace(/_/g, " ")
					.toLowerCase()
					.replace(/\b\w/g, (l) => l.toUpperCase())
			}))
			.sort((a, b) => b.challan_count - a.challan_count);

		const offenceNames = offenceArray.map((item) => item.formattedName);
		const offenceCounts = offenceArray.map((item) => item.challan_count);

		return {
			color: [
				"#1976D2",
				"#2196F3",
				"#03A9F4",
				"#00BCD4",
				"#009688",
				"#4CAF50",
				"#8BC34A",
				"#FF9800",
				"#E91E63",
				"#9C27B0"
			],
			tooltip: {
				trigger: "axis",
				confine: true,
				axisPointer: {
					type: "shadow"
				},
				formatter: (params: any) => {
					const data = params[0];
					// Use axisValue to get the full offence name (before truncation)
					const fullOffenceName = data.axisValue || data.name;

					return `
						<div style="padding: 8px; max-width: 350px; box-sizing: border-box;">
							<div style="font-weight: 600; margin-bottom: 4px; max-width: 100%; word-wrap: break-word; word-break: break-word; overflow-wrap: break-word; white-space: normal; line-height: 1.4;">${fullOffenceName}</div>
							<div style="display: flex; align-items: center; gap: 8px; margin-top: 4px;">
								<div style="width: 12px; height: 12px; background: ${data.color}; border-radius: 2px; flex-shrink: 0;"></div>
								<span>Challans: <strong>${formatNumberMetric(data.value)}</strong></span>
							</div>
						</div>
					`;
				}
			},
			grid: { left: 0, right: 25, bottom: 0, containLabel: true },
			xAxis: {
				type: "category",
				data: offenceNames,
				axisLine: { show: false },
				axisLabel: {
					rotate: 45,
					interval: 0,
					fontSize: 11,
					width: 120,
					overflow: "truncate",
					ellipsis: "...",
					formatter: (value: string) => {
						// Truncate to 25 characters for 45-degree rotated labels
						if (value.length > 25) {
							return value.substring(0, 25) + "...";
						}
						return value;
					}
				}
			},
			yAxis: {
				type: "value",
				axisLine: { show: false },
				axisLabel: { formatter: (value: number) => formatNumberMetric(value) },
				splitLine: { lineStyle: { color: "#f0f0f0" } }
			},
			series: [
				{
					name: "Challan Count",
					type: "bar",
					data: offenceCounts,
					barWidth: "60%",
					itemStyle: {
						borderRadius: [4, 4, 0, 0]
					}
				}
			]
		};
	}, [challanStats]);

	// Interceptor Violation Statistics Donut Chart (using interceptor challan stats)
	const interceptorViolationStatisticsOptions = useMemo(() => {
		if (!interceptorChallanStats?.interceptor_statistics?.length) {
			return {
				color: [
					"#1976D2",
					"#2196F3",
					"#03A9F4",
					"#00BCD4",
					"#009688",
					"#4CAF50",
					"#8BC34A",
					"#FF9800",
					"#E91E63",
					"#9C27B0"
				],
				tooltip: { trigger: "item" },
				legend: { show: false },
				series: [{ type: "pie", radius: ["50%", "80%"], data: [] }]
			};
		}

		// Use challan_count directly from interceptor challan stats
		const interceptorData = interceptorChallanStats.interceptor_statistics
			.map((interceptor) => ({
				interceptor_id: interceptor.interceptor_id,
				interceptor_numberplate: interceptor.interceptor_numberplate,
				interceptor_location: interceptor.interceptor_location,
				challan_count: interceptor.challan_count
			}))
			.filter((item) => item.challan_count > 0)
			.sort((a, b) => b.challan_count - a.challan_count);

		return {
			color: [
				"#1976D2",
				"#2196F3",
				"#03A9F4",
				"#00BCD4",
				"#009688",
				"#4CAF50",
				"#8BC34A",
				"#FF9800",
				"#E91E63",
				"#9C27B0"
			],
			tooltip: {
				trigger: "item",
				formatter: (params: any) => {
					const data = params;
					return `
						<div style="padding: 8px;">
							<div style="font-weight: 600; margin-bottom: 4px;">${data.name}</div>
							<div style="display: flex; align-items: center; gap: 8px; margin-bottom: 2px;">
								<div style="width: 12px; height: 12px; background: ${data.color}; border-radius: 2px;"></div>
								<span>Challans: <strong>${formatNumberMetric(data.value)}</strong></span>
							</div>
							<div style="color: #666; font-size: 12px;">${formatNumberMetric(data.percent)}% of total</div>
						</div>
					`;
				}
			},
			legend: {
				show: false
			},
			series: [
				{
					type: "pie",
					radius: ["50%", "80%"],
					avoidLabelOverlap: false,
					label: { show: false },
					emphasis: {
						itemStyle: {
							shadowBlur: 10,
							shadowOffsetX: 0,
							shadowColor: "rgba(0, 0, 0, 0.5)"
						}
					},
					data: interceptorData.map((item) => ({
						value: item.challan_count,
						name: `${item.interceptor_numberplate} (${item.interceptor_location})`
					}))
				}
			]
		};
	}, [interceptorChallanStats]);

	// Jurisdiction Violation Statistics Horizontal Bar Chart
	/* const jurisdictionViolationStatisticsOptions = useMemo(
		() => ({
			color: [
				"#1976D2",
				"#2196F3",
				"#03A9F4",
				"#00BCD4",
				"#009688",
				"#4CAF50",
				"#8BC34A",
				"#FF9800",
				"#E91E63",
				"#9C27B0"
			],
			tooltip: {
				trigger: "axis",
				axisPointer: {
					type: "shadow"
				},
				formatter: (params: any) => {
					const data = params[0];
					const jurisdictionData = jurisdictionViolationStatisticsData?.jurisdiction_statistics.find(
						(item) => item.jurisdiction_name === data.name
					);
					return `
						<div style="padding: 8px;">
							<div style="font-weight: 600; margin-bottom: 4px;">${data.name}</div>
							<div style="display: flex; align-items: center; gap: 8px; margin-bottom: 2px;">
								<div style="width: 12px; height: 12px; background: ${data.color}; border-radius: 2px;"></div>
								<span>Violations: <strong>${data.value}</strong></span>
							</div>
							<div style="color: #666; font-size: 12px;">
								RTO Code: ${jurisdictionData?.jurisdiction_rto_code || "N/A"} | 
								Interceptors: ${jurisdictionData?.interceptor_count || 0}
							</div>
						</div>
					`;
				}
			},
			grid: { left: 0, right: 25, bottom: 0, containLabel: true },
			xAxis: {
				type: "value",
				axisLine: { show: false },
				splitLine: { lineStyle: { color: "#f0f0f0" } }
			},
			yAxis: {
				type: "category",
				data: jurisdictionViolationStatisticsData?.jurisdiction_statistics.map((item) => item.jurisdiction_name) || [],
				axisLine: { show: false },
				axisLabel: {
					fontSize: 12,
					formatter: (value: string) => {
						// Truncate long jurisdiction names
						return value.length > 15 ? value.substring(0, 15) + "..." : value;
					}
				}
			},
			series: [
				{
					name: "Violation Count",
					type: "bar",
					data: jurisdictionViolationStatisticsData?.jurisdiction_statistics.map((item) => item.violation_count) || [],
					barWidth: "60%",
					itemStyle: {
						borderRadius: [0, 4, 4, 0]
					}
				}
			]
		}),
		[jurisdictionViolationStatisticsData]
	); */

	// Calculate interceptor statistics with challan counts for custom legend
	const interceptorOffenceStatisticsForLegend = useMemo(() => {
		if (!interceptorChallanStats?.interceptor_statistics?.length) return [];

		// Use challan_count directly from interceptor challan stats
		return interceptorChallanStats.interceptor_statistics
			.map((interceptor) => ({
				interceptor_id: interceptor.interceptor_id,
				interceptor_numberplate: interceptor.interceptor_numberplate,
				interceptor_location: interceptor.interceptor_location,
				total_offences: interceptor.challan_count
			}))
			.filter((item) => item.total_offences > 0)
			.sort((a, b) => b.total_offences - a.total_offences);
	}, [interceptorChallanStats]);

	// Calculate offence statistics for custom legend
	const offenceStatisticsForLegend = useMemo(() => {
		if (!challanStats?.jurisdiction_statistics?.length) return [];

		// Aggregate offence data from all jurisdictions
		const offenceMap = new Map<string, number>();
		challanStats.jurisdiction_statistics.forEach((jurisdiction) => {
			jurisdiction.offence_breakdown?.forEach((offence) => {
				const currentCount = offenceMap.get(offence.offence_name) || 0;
				offenceMap.set(offence.offence_name, currentCount + offence.challan_count);
			});
		});

		// Convert to array and sort by count (descending)
		return Array.from(offenceMap.entries())
			.map(([offence_name, challan_count]) => ({
				offence_name,
				formattedName: offence_name
					.replace(/_/g, " ")
					.toLowerCase()
					.replace(/\b\w/g, (l) => l.toUpperCase()),
				challan_count
			}))
			.sort((a, b) => b.challan_count - a.challan_count);
	}, [challanStats]);

	// Challan Statistics Stacked Bar Chart
	const challanStatisticsOptions = useMemo(() => {
		if (!challanStats?.jurisdiction_statistics?.length) {
			return {
				color: [
					"#1976D2",
					"#2196F3",
					"#03A9F4",
					"#00BCD4",
					"#009688",
					"#4CAF50",
					"#8BC34A",
					"#FF9800",
					"#E91E63",
					"#9C27B0"
				],
				tooltip: { trigger: "axis" },
				grid: { left: 0, right: 25, bottom: 0, containLabel: true },
				xAxis: { type: "category", data: [], axisLine: { show: false } },
				yAxis: { type: "value", axisLine: { show: false }, splitLine: { lineStyle: { color: "#f0f0f0" } } },
				series: []
			};
		}

		// Get all unique offence names across all jurisdictions
		const allOffenceNames = new Set<string>();
		challanStats.jurisdiction_statistics.forEach((jurisdiction) => {
			jurisdiction.offence_breakdown?.forEach((offence) => {
				allOffenceNames.add(offence.offence_name);
			});
		});

		const offenceNamesArray = Array.from(allOffenceNames);
		const jurisdictions = challanStats.jurisdiction_statistics.map((item) => item.jurisdiction_name);

		// Create series for each offence type
		const series = offenceNamesArray.map((offenceName) => {
			const data = jurisdictions.map((jurisdictionName) => {
				const jurisdiction = challanStats.jurisdiction_statistics.find((j) => j.jurisdiction_name === jurisdictionName);
				const offence = jurisdiction?.offence_breakdown?.find((o) => o.offence_name === offenceName);
				return offence?.challan_count || 0;
			});

			return {
				name: offenceName
					.replace(/_/g, " ")
					.toLowerCase()
					.replace(/\b\w/g, (l) => l.toUpperCase()),
				type: "bar",
				stack: "total",
				data,
				// barWidth: "60%"
				barMaxWidth: 100
			};
		});

		return {
			color: [
				"#1976D2",
				"#2196F3",
				"#03A9F4",
				"#00BCD4",
				"#009688",
				"#4CAF50",
				"#8BC34A",
				"#FF9800",
				"#E91E63",
				"#9C27B0",
				"#F44336",
				"#795548",
				"#607D8B"
			],
			tooltip: {
				trigger: "axis",
				axisPointer: {
					type: "shadow"
				},
				formatter: (params: any) => {
					if (!Array.isArray(params)) return "";
					const jurisdictionName = params[0].axisValue;
					const jurisdictionData = challanStats?.jurisdiction_statistics.find(
						(item) => item.jurisdiction_name === jurisdictionName
					);

					let tooltipContent = `
						<div style="padding: 8px;">
							<div style="font-weight: 600; margin-bottom: 8px;">${jurisdictionName}</div>
					`;

					let totalChallans = 0;
					params.forEach((param: any) => {
						if (param.value > 0) {
							tooltipContent += `
								<div style="display: flex; align-items: center; gap: 8px; margin-bottom: 4px;">
									<div style="width: 12px; height: 12px; background: ${param.color}; border-radius: 2px;"></div>
									<span>${param.seriesName}: <strong>${formatNumberMetric(param.value)}</strong></span>
								</div>
							`;
							totalChallans += param.value;
						}
					});

					tooltipContent += `
							<div style="margin-top: 8px; padding-top: 8px; border-top: 1px solid #e0e0e0;">
								<div style="font-weight: 600; margin-bottom: 4px;">Total: <strong>${formatNumberMetric(totalChallans)}</strong></div>
								<div style="color: #666; font-size: 12px;">
									RTO Code: ${jurisdictionData?.jurisdiction_rto_code || "N/A"} | 
									Fine Amount: ₹${formatNumberMetric(jurisdictionData?.total_fine_amount || 0)} | 
									Interceptors: ${formatNumberMetric(jurisdictionData?.interceptor_count || 0)}
								</div>
							</div>
						</div>
					`;

					return tooltipContent;
				}
			},
			legend: {
				show: false
			},
			grid: { left: 0, right: 25, bottom: 0, containLabel: true },
			xAxis: {
				type: "category",
				data: jurisdictions,
				axisLine: { show: false },
				axisLabel: {
					rotate: 45,
					interval: 0,
					fontSize: 12,
					formatter: (value: string) => {
						return value.length > 15 ? value.substring(0, 15) + "..." : value;
					}
				}
			},
			yAxis: {
				type: "value",
				axisLine: { show: false },
				axisLabel: { formatter: (value: number) => formatNumberMetric(value) },
				splitLine: { lineStyle: { color: "#f0f0f0" } }
			},
			series
		};
	}, [challanStats]);

	// PDF versions of chart options with labels enabled
	const pdfViolationTypeStatisticsOptions = useMemo(() => {
		const base = violationTypeStatisticsOptions;
		return {
			...base,
			series: base.series.map((series: any) => ({
				...series,
				label: {
					show: true,
					position: "top",
					fontSize: 11,
					color: "#000",
					fontWeight: "bold",
					formatter: (params: any) => formatNumberMetric(params.value)
				}
			}))
		};
	}, [violationTypeStatisticsOptions]);

	const pdfInterceptorViolationStatisticsOptions = useMemo(() => {
		const base = interceptorViolationStatisticsOptions;
		return {
			...base,
			series: base.series.map((series: any) => ({
				...series,
				label: {
					show: true,
					position: "outside",
					formatter: (params: any) => `${params.name}: ${formatNumberMetric(params.value)} (${formatNumberMetric(params.percent)}%)`,
					fontSize: 11,
					color: "#000",
					fontWeight: "bold",
					overflow: "break"
				},
				labelLine: {
					show: true,
					length: 15,
					length2: 25,
					lineStyle: {
						color: "#666",
						width: 1
					}
				}
			}))
			// legend: {
			// 	show: true,
			// 	orient: "vertical",
			// 	left: "right",
			// 	top: "middle",
			// 	textStyle: {
			// 		fontSize: 11
			// 	}
			// }
		};
	}, [interceptorViolationStatisticsOptions]);

	// const pdfJurisdictionViolationStatisticsOptions = useMemo(() => {
	// 	const base = jurisdictionViolationStatisticsOptions;
	// 	return {
	// 		...base,
	// 		series: base.series.map((series: any) => ({
	// 			...series,
	// 			label: {
	// 				show: true,
	// 				position: "right",
	// 				fontSize: 11,
	// 				color: "#000",
	// 				fontWeight: "bold",
	// 				formatter: "{c}"
	// 			}
	// 		}))
	// 	};
	// }, [jurisdictionViolationStatisticsOptions]);

	const pdfChallanStatisticsOptions = useMemo(() => {
		const base = challanStatisticsOptions;
		return {
			...base,
			series: base.series.map((series: any) => ({
				...series,
				label: {
					show: true,
					position: "inside",
					fontSize: 10,
					color: "#000",
					fontWeight: "bold",
					formatter: (params: any) => {
						return params.value > 0 ? formatNumberMetric(params.value) : "";
					}
				}
			}))
		};
	}, [challanStatisticsOptions]);

	// Comprehensive Analytics Summary Cards
	const comprehensiveSummaryStats = useMemo(
		() => [
			{
				icon: <DescriptionIcon fontSize="medium" />,
				label: "Total Challans",
				value: formatNumberMetric(challanMetrics?.total_challans ?? 0),
				gradient: "linear-gradient(135deg, #E0F0FF 0%, #F3EFFF 100%)",
				iconBg: "#E0F0FF",
				iconColor: "#388DFF"
			},
			{
				icon: <SpeedIcon fontSize="medium" />,
				label: "Offence Types",
				value: formatNumberMetric(challanMetrics?.total_offence_types ?? 0),
				gradient: "linear-gradient(135deg, #FEE8E8 0%, #F8D7E3 100%)",
				iconBg: "#FEE8E8",
				iconColor: "#6A040F"
			},
			{
				icon: <DirectionsCarIcon fontSize="medium" />,
				label: "Interceptors",
				value: formatNumberMetric(challanMetrics?.total_interceptors ?? 0),
				gradient: "linear-gradient(135deg, #E8F5E9 0%, #D0F2DF 100%)",
				iconBg: "#E8F5E9",
				iconColor: "#43A047"
			},
			{
				icon: <AccountBalanceWalletIcon fontSize="medium" />,
				label: "RTOs",
				value: formatNumberMetric(challanMetrics?.total_rto_jurisdictions ?? 0),
				gradient: "linear-gradient(135deg, #FFF3E0 0%, #FFE0B2 100%)",
				iconBg: "#FFF3E0",
				iconColor: "#FF9800"
			}
		],
		[challanMetrics]
	);

	return (
		<Box className="page-content-wrapper">
			<Box sx={{ flex: 1, height: 0, display: "flex", flexDirection: "column", overflowY: "auto" }}>
				<Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
					<LoadingButton
						disableElevation
						variant="contained"
						startIcon={<DownloadIcon />}
						onClick={handleDownloadPDF}
						sx={{ textTransform: "none" }}
						loading={isGeneratingPDF}
						loadingPosition="start"
					>
						{isGeneratingPDF ? "Generating PDF..." : "Download PDF"}
					</LoadingButton>
				</Box>
				<Box ref={pdfRef}>
					<Grid2 container spacing={2}>
						{/* Comprehensive Analytics Summary Cards */}
						{comprehensiveSummaryStats.map((stat) => (
							<Grid2 key={stat.label} size={{ xs: 12, md: 3 }}>
								{challanMetricsLoading || challanMetricsFetching ? (
									<Box className="dashboard-summary-stats-card" sx={{ background: stat.gradient }}>
										<Skeleton variant="circular" width={48} height={48} />
										<Box sx={{ flex: 1 }}>
											<Skeleton variant="text" width="60%" height={20} />
											<Skeleton variant="text" width="40%" height={32} />
										</Box>
									</Box>
								) : (
									<Box className="dashboard-summary-stats-card" sx={{ background: stat.gradient }}>
										<Avatar
											classes={{ root: "dashboard-summary-stat-avatar-root" }}
											sx={{ bgcolor: stat.iconBg, color: stat.iconColor }}
										>
											{stat.icon}
										</Avatar>

										<Box>
											<Typography
												variant="body2"
												color="text.secondary"
												sx={{ fontWeight: 500, letterSpacing: 0.2, mb: 0.5 }}
											>
												{stat.label}
											</Typography>

											<Typography
												variant="h4"
												fontWeight={700}
												color="text.primary"
												sx={{ letterSpacing: -1, lineHeight: 1.1 }}
											>
												{stat.value}
											</Typography>
										</Box>
									</Box>
								)}
							</Grid2>
						))}

						{/* Top Performers Section */}
						<Grid2 size={{ xs: 12, md: 6 }}>
							<Box className="dashboard-graph-card">
								<Typography variant="h6" fontWeight={700} mb={1} sx={{ fontSize: 20, letterSpacing: -0.5 }}>
									Top Interceptors
								</Typography>

								<Typography variant="body2" color="text.secondary" mb={2} sx={{ fontWeight: 400, fontSize: 14 }}>
									Highest performing interceptors by challan count
								</Typography>

								{challanComprehensiveStatsLoading || challanComprehensiveStatsFetching ? (
									<Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
										{Array.from({ length: 5 }).map((_, index) => (
											<Box key={index} sx={{ display: "flex", alignItems: "center", gap: 2 }}>
												<Skeleton variant="circular" width={32} height={32} />
												<Box sx={{ flex: 1 }}>
													<Skeleton variant="text" width="60%" height={20} />
													<Skeleton variant="text" width="40%" height={16} />
												</Box>
												<Skeleton variant="text" width={40} height={20} />
											</Box>
										))}
									</Box>
								) : !challanComprehensiveStats?.top_interceptors?.length ? (
									<Box sx={{ textAlign: "center", py: 4 }}>
										<Typography variant="h6" color="text.secondary">
											No interceptor data available
										</Typography>
									</Box>
								) : (
									<Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
										{challanComprehensiveStats.top_interceptors.slice(0, 5).map((interceptor, index) => (
											<Box key={interceptor.interceptor_id} className="top-interceptor-card-wrapper">
												<Box className="top-interceptor-card-index-wrapper">{index + 1}</Box>

												<Box sx={{ flex: 1 }}>
													<Typography variant="body1" fontWeight={600}>
														{interceptor.interceptor_numberplate}
													</Typography>

													<Typography variant="body2" color="text.secondary">
														{`${formatNumberMetric(interceptor.challan_count)} challans`}
													</Typography>
												</Box>

												{index === 0 ? <Box className="top-interceptor-card-badge">TOP</Box> : null}
											</Box>
										))}
									</Box>
								)}
							</Box>
						</Grid2>

						{/* Top Jurisdictions Section */}
						<Grid2 size={{ xs: 12, md: 6 }}>
							<Box className="dashboard-graph-card">
								<Typography variant="h6" fontWeight={700} mb={1} sx={{ fontSize: 20, letterSpacing: -0.5 }}>
									Top Jurisdictions
								</Typography>

								<Typography variant="body2" color="text.secondary" mb={2} sx={{ fontWeight: 400, fontSize: 14 }}>
									Highest performing RTO jurisdictions by challan count
								</Typography>

								{challanComprehensiveStatsLoading || challanComprehensiveStatsFetching ? (
									<Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
										{Array.from({ length: 5 }).map((_, index) => (
											<Box key={index} sx={{ display: "flex", alignItems: "center", gap: 2 }}>
												<Skeleton variant="circular" width={32} height={32} />
												<Box sx={{ flex: 1 }}>
													<Skeleton variant="text" width="70%" height={20} />
													<Skeleton variant="text" width="50%" height={16} />
												</Box>
												<Skeleton variant="text" width={40} height={20} />
											</Box>
										))}
									</Box>
								) : !challanComprehensiveStats?.top_jurisdictions?.length ? (
									<Box sx={{ textAlign: "center", py: 4 }}>
										<Typography variant="h6" color="text.secondary">
											No jurisdiction data available
										</Typography>
									</Box>
								) : (
									<Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
										{challanComprehensiveStats.top_jurisdictions.slice(0, 5).map((jurisdiction, index) => (
											<Box key={jurisdiction.jurisdiction_id} className="top-jurisdiction-card-wrapper">
												<Box className="top-jurisdiction-card-index-wrapper">{index + 1}</Box>

												<Box sx={{ flex: 1 }}>
													<Typography variant="body1" fontWeight={600}>
														{jurisdiction.jurisdiction_name}
													</Typography>

													<Typography variant="body2" color="text.secondary">
														{`${jurisdiction.jurisdiction_rto_code} • ${formatNumberMetric(jurisdiction.challan_count)} challans`}
													</Typography>
												</Box>

												{index === 0 ? <Box className="top-jurisdiction-card-badge">TOP</Box> : null}
											</Box>
										))}
									</Box>
								)}
							</Box>
						</Grid2>

						{/* E-Challan Status Overview Line Chart */}
						{/* <Grid2 size={{ xs: 12, md: 8 }}>
							<Box className="dashboard-graph-card">
								<Box
									sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2, px: 1, pt: 1 }}
								>
									<Box>
										<Typography variant="h6" fontWeight={700} sx={{ fontSize: 20, letterSpacing: -0.5, mb: 0.2 }}>
											E-Challan Status Overview
										</Typography>

										<Typography variant="body2" color="text.secondary" sx={{ fontWeight: 400, fontSize: 14 }}>
											Challan status distribution over time
										</Typography>
									</Box>
								</Box>

								<ReactECharts option={eChallanStatusOptions} style={{ minHeight: "200px", width: "100%" }} />
							</Box>
						</Grid2> */}

						{/* Aging Analysis Donut Chart */}
						{/* <Grid2 size={{ xs: 12, md: 4 }}>
							<Box className="dashboard-graph-card">
								<Typography variant="h6" fontWeight={700} mb={1} sx={{ fontSize: 20, letterSpacing: -0.5 }}>
									Aging Analysis
								</Typography>

								<Typography variant="body2" color="text.secondary" mb={2} sx={{ fontWeight: 400, fontSize: 14 }}>
									Revenue breakdown by aging period
								</Typography>

								<Box sx={{ position: "relative", flex: 1 }}>
									<ReactECharts option={agingAnalysisOptions} style={{ minHeight: "200px", width: "100%" }} />

									<Box
										sx={{
											position: "absolute",
											top: "50%",
											left: "50%",
											transform: "translate(-50%, -50%)",
											maxWidth: "60%"
										}}
									>
										<Typography fontSize="32px" textAlign="center" color="#00000099" fontWeight={500}>
											9,937
										</Typography>

										<Typography textAlign="center" fontWeight={500} color="#00000099">
											Revenue Breakdown
										</Typography>
									</Box>
								</Box>

								<Box sx={{ display: "flex", justifyContent: "center", gap: 2, mt: 1, flexWrap: "wrap" }}>
									<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
										<Box sx={{ width: 12, height: 12, borderRadius: "50%", bgcolor: "#FF9800" }} />
										<Typography variant="body2">0-30 days: 65%</Typography>
									</Box>

									<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
										<Box sx={{ width: 12, height: 12, borderRadius: "50%", bgcolor: "#43A047" }} />
										<Typography variant="body2">31-60 days: 20%</Typography>
									</Box>

									<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
										<Box sx={{ width: 12, height: 12, borderRadius: "50%", bgcolor: "#1976D2" }} />
										<Typography variant="body2">61-90 days: 10%</Typography>
									</Box>

									<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
										<Box sx={{ width: 12, height: 12, borderRadius: "50%", bgcolor: "#9C27B0" }} />
										<Typography variant="body2">90+ days: 5%</Typography>
									</Box>
								</Box>
							</Box>
						</Grid2> */}

						{/* Violation Type Statistics Bar Chart */}
						<Grid2 size={{ xs: 12, md: 6 }}>
							<Box className="dashboard-graph-card">
								<Typography variant="h6" fontWeight={700} mb={1} sx={{ fontSize: 20, letterSpacing: -0.5 }}>
									Offence Type Statistics
								</Typography>

								<Typography variant="body2" color="text.secondary" mb={2} sx={{ fontWeight: 400, fontSize: 14 }}>
									Distribution of challans by offence type
								</Typography>

								{getChallanStatsLoading || getChallanStatsFetching ? (
									<Box sx={{ minHeight: "300px", width: "100%", display: "flex", flexDirection: "column", gap: 2 }}>
										<Skeleton variant="rectangular" height={250} />
										<Skeleton variant="text" width="30%" />
										<Skeleton variant="text" width="50%" />
									</Box>
								) : !challanStats?.jurisdiction_statistics?.length ? (
									<Box className="violation-card-no-data-wrapper">
										<Typography variant="h6" color="text.secondary">
											No offence data available
										</Typography>
									</Box>
								) : (
									<ReactECharts
										ref={violationChartRef}
										option={violationTypeStatisticsOptions}
										style={{ minHeight: "350px", width: "100%" }}
									/>
								)}
							</Box>
						</Grid2>

						{/* Interceptor Violation Statistics Donut Chart */}
						<Grid2 size={{ xs: 12, md: 6 }}>
							<Box className="dashboard-graph-card">
								<Typography variant="h6" fontWeight={700} mb={1} sx={{ fontSize: 20, letterSpacing: -0.5 }}>
									Interceptor Violation Statistics
								</Typography>

								<Typography variant="body2" color="text.secondary" mb={2} sx={{ fontWeight: 400, fontSize: 14 }}>
									Total challans per interceptor
								</Typography>

								{interceptorChallanStatsLoading || interceptorChallanStatsFetching ? (
									<Box sx={{ position: "relative", flex: 1 }}>
										<Skeleton variant="circular" width={200} height={200} sx={{ mx: "auto" }} />
										<Box sx={{ display: "flex", justifyContent: "center", gap: 2, mt: 2 }}>
											<Skeleton variant="text" width={60} />
											<Skeleton variant="text" width={60} />
											<Skeleton variant="text" width={60} />
										</Box>
									</Box>
								) : !interceptorChallanStats?.interceptor_statistics?.length ? (
									<Box className="interceptor-card-no-data-wrapper">
										<Typography variant="h6" color="text.secondary">
											No interceptor data available
										</Typography>
									</Box>
								) : (
									<>
										<Box sx={{ position: "relative", flex: 1 }}>
											<ReactECharts
												ref={interceptorChartRef}
												option={interceptorViolationStatisticsOptions}
												style={{ minHeight: "250px", width: "100%", height: "100%" }}
											/>

											<Box
												sx={{
													position: "absolute",
													top: "50%",
													left: "50%",
													transform: "translate(-50%, -50%)",
													maxWidth: "60%"
												}}
											>
												<Typography fontSize="24px" textAlign="center" color="#00000099" fontWeight={500}>
													{formatNumberMetric(
														interceptorOffenceStatisticsForLegend.reduce((sum, item) => sum + item.total_offences, 0)
													)}
												</Typography>

												<Typography textAlign="center" fontWeight={500} color="#00000099" fontSize="12px">
													Total Challans
												</Typography>
											</Box>
										</Box>

										<Box sx={{ display: "flex", justifyContent: "center", gap: 2, mt: 1, flexWrap: "wrap" }}>
											{interceptorOffenceStatisticsForLegend.slice(0, 4).map((interceptor, index) => {
												const colors = ["#1976D2", "#2196F3", "#03A9F4", "#00BCD4"];
												const totalOffences = interceptorOffenceStatisticsForLegend.reduce(
													(sum, item) => sum + item.total_offences,
													0
												);
												const percentage = totalOffences
													? formatNumberMetric((interceptor.total_offences / totalOffences) * 100, 1)
													: "0";
												const displayName = `${interceptor.interceptor_numberplate} (${interceptor.interceptor_location})`;

												return (
													<Box
														key={interceptor.interceptor_id}
														sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
													>
														<Box sx={{ width: 12, height: 12, borderRadius: "50%", bgcolor: colors[index] }} />
														{displayName.length > 25 ? (
															<Tooltip arrow placement="top" title={displayName}>
																<Typography variant="body2" sx={{ fontSize: "0.75rem", cursor: "help" }}>
																	{`${displayName.substring(0, 25)}...`}: {percentage}%
																</Typography>
															</Tooltip>
														) : (
															<Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
																{displayName}: {percentage}%
															</Typography>
														)}
													</Box>
												);
											})}
											{interceptorOffenceStatisticsForLegend.length > 4 && (
												<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
													<Box sx={{ width: 12, height: 12, borderRadius: "50%", bgcolor: "#9C27B0" }} />

													<Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
														{`+${formatNumberMetric(interceptorOffenceStatisticsForLegend.length - 4)} more`}
													</Typography>
												</Box>
											)}
										</Box>
									</>
								)}
							</Box>
						</Grid2>

						{/* Jurisdiction Violation Statistics Horizontal Bar Chart */}
						{/* <Grid2 size={{ xs: 12, md: 6 }}>
							<Box className="dashboard-graph-card">
								<Typography variant="h6" fontWeight={700} mb={1} sx={{ fontSize: 20, letterSpacing: -0.5 }}>
									Jurisdiction Violation Statistics
								</Typography>

								<Typography variant="body2" color="text.secondary" mb={2} sx={{ fontWeight: 400, fontSize: 14 }}>
									Violations by RTO jurisdiction
								</Typography>

								{jurisdictionViolationStatisticsLoading || jurisdictionViolationStatisticsFetching ? (
									<Box sx={{ minHeight: "300px", width: "100%", display: "flex", flexDirection: "column", gap: 2 }}>
										<Skeleton variant="rectangular" height={250} />
										<Skeleton variant="text" width="30%" />
										<Skeleton variant="text" width="50%" />
									</Box>
								) : !jurisdictionViolationStatisticsData?.jurisdiction_statistics?.length ? (
									<Box className="jurisdiction-card-no-data-wrapper">
										<Typography variant="h6" color="text.secondary">
											No jurisdiction data available
										</Typography>
									</Box>
								) : (
									<ReactECharts
										ref={jurisdictionChartRef}
										option={jurisdictionViolationStatisticsOptions}
										style={{ minHeight: "300px", width: "100%" }}
									/>
								)}
							</Box>
						</Grid2> */}

						{/* Challan Statistics Stacked Bar Chart */}
						<Grid2 size={{ xs: 12 }}>
							<Box className="dashboard-graph-card">
								<Typography variant="h6" fontWeight={700} mb={1} sx={{ fontSize: 20, letterSpacing: -0.5 }}>
									Challan Statistics
								</Typography>

								<Typography variant="body2" color="text.secondary" mb={2} sx={{ fontWeight: 400, fontSize: 14 }}>
									Challan breakdown by offence type and jurisdiction
								</Typography>

								{getChallanStatsLoading || getChallanStatsFetching ? (
									<Box sx={{ minHeight: "300px", width: "100%", display: "flex", flexDirection: "column", gap: 2 }}>
										<Skeleton variant="rectangular" height={250} />
										<Skeleton variant="text" width="30%" />
										<Skeleton variant="text" width="50%" />
									</Box>
								) : !challanStats?.jurisdiction_statistics?.length ? (
									<Box className="jurisdiction-card-no-data-wrapper">
										<Typography variant="h6" color="text.secondary">
											No challan data available
										</Typography>
									</Box>
								) : (
									<>
										<ReactECharts
											ref={challanStatsChartRef}
											option={challanStatisticsOptions}
											style={{ minHeight: "400px", width: "100%" }}
										/>

										<Box sx={{ display: "flex", justifyContent: "center", gap: 2, mt: 2, flexWrap: "wrap" }}>
											{offenceStatisticsForLegend.slice(0, 6).map((offence, index) => {
												const colors = [
													"#1976D2",
													"#2196F3",
													"#03A9F4",
													"#00BCD4",
													"#009688",
													"#4CAF50",
													"#8BC34A",
													"#FF9800",
													"#E91E63",
													"#9C27B0",
													"#F44336",
													"#795548",
													"#607D8B"
												];
												const totalChallans = challanStats?.total_challans || 0;
												const percentage = totalChallans
													? formatNumberMetric((offence.challan_count / totalChallans) * 100, 1)
													: "0";

												return (
													<Box key={offence.offence_name} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
														<Box
															sx={{
																width: 12,
																height: 12,
																borderRadius: "2px",
																bgcolor: colors[index % colors.length]
															}}
														/>
														{offence.formattedName.length > 25 ? (
															<Tooltip arrow placement="top" title={offence.formattedName}>
																<Typography variant="body2" sx={{ fontSize: "0.75rem", cursor: "help" }}>
																	{`${offence.formattedName.substring(0, 25)}...`}: {percentage}%
																</Typography>
															</Tooltip>
														) : (
															<Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
																{offence.formattedName}: {percentage}%
															</Typography>
														)}
													</Box>
												);
											})}
											{offenceStatisticsForLegend.length > 6 && (
												<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
													<Box
														sx={{
															width: 12,
															height: 12,
															borderRadius: "2px",
															bgcolor: "#9C27B0"
														}}
													/>
													<Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
														{`+${formatNumberMetric(offenceStatisticsForLegend.length - 6)} more`}
													</Typography>
												</Box>
											)}
										</Box>
									</>
								)}
							</Box>
						</Grid2>

						{/* Challan Revenue Breakdown by Violation Stacked Bar Chart */}
						{/* <Grid2 size={{ xs: 12, md: 6 }}>
							<Box className="dashboard-graph-card">
								<Typography variant="h6" fontWeight={700} mb={1} sx={{ fontSize: 20, letterSpacing: -0.5 }}>
									Challan Revenue Breakdown by Violation
								</Typography>

								<Typography variant="body2" color="text.secondary" mb={2} sx={{ fontWeight: 400, fontSize: 14 }}>
									Revenue distribution by violation type and time
								</Typography>

								<ReactECharts option={revenueBreakdownOptions} style={{ minHeight: "300px", width: "100%" }} />
							</Box>
						</Grid2> */}
					</Grid2>
				</Box>
			</Box>
			{/* Hidden div for PDF generation with labels */}
			<Box
				ref={pdfPrintRef}
				sx={{
					position: "absolute",
					left: "-9999px",
					top: 0,
					width: "100%",
					maxWidth: "1200px",
					backgroundColor: "#ffffff"
				}}
			>
				<Grid2 container spacing={2}>
					{/* Comprehensive Analytics Summary Cards */}
					{comprehensiveSummaryStats.map((stat) => (
						<Grid2
							key={stat.label}
							size={{ xs: 12, md: 3 }}
							sx={{ border: "1px solid #e0e0e0", borderRadius: "0.5rem" }}
						>
							{challanMetricsLoading || challanMetricsFetching ? (
								<Box className="dashboard-summary-stats-card" sx={{ background: stat.gradient }}>
									<Skeleton variant="circular" width={48} height={48} />
									<Box sx={{ flex: 1 }}>
										<Skeleton variant="text" width="60%" height={20} />
										<Skeleton variant="text" width="40%" height={32} />
									</Box>
								</Box>
							) : (
								<Box className="dashboard-summary-stats-card" sx={{ background: stat.gradient }}>
									<Avatar
										classes={{ root: "dashboard-summary-stat-avatar-root" }}
										sx={{ bgcolor: stat.iconBg, color: stat.iconColor }}
									>
										{stat.icon}
									</Avatar>

									<Box>
										<Typography
											variant="body2"
											color="text.secondary"
											sx={{ fontWeight: 500, letterSpacing: 0.2, mb: 0.5 }}
										>
											{stat.label}
										</Typography>

										<Typography
											variant="h4"
											fontWeight={700}
											color="text.primary"
											sx={{ letterSpacing: -1, lineHeight: 1.1 }}
										>
											{stat.value}
										</Typography>
									</Box>
								</Box>
							)}
						</Grid2>
					))}

					{/* Top Performers Section */}
					<Grid2 size={{ xs: 12, md: 6 }} sx={{ border: "1px solid #e0e0e0", borderRadius: "0.5rem" }}>
						<Box className="dashboard-graph-card">
							<Typography variant="h6" fontWeight={700} mb={1} sx={{ fontSize: 20, letterSpacing: -0.5 }}>
								Top Interceptors
							</Typography>

							<Typography variant="body2" color="text.secondary" mb={2} sx={{ fontWeight: 400, fontSize: 14 }}>
								Highest performing interceptors by challan count
							</Typography>

							{challanComprehensiveStatsLoading || challanComprehensiveStatsFetching ? (
								<Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
									{Array.from({ length: 5 }).map((_, index) => (
										<Box key={index} sx={{ display: "flex", alignItems: "center", gap: 2 }}>
											<Skeleton variant="circular" width={32} height={32} />
											<Box sx={{ flex: 1 }}>
												<Skeleton variant="text" width="60%" height={20} />
												<Skeleton variant="text" width="40%" height={16} />
											</Box>
											<Skeleton variant="text" width={40} height={20} />
										</Box>
									))}
								</Box>
							) : !challanComprehensiveStats?.top_interceptors?.length ? (
								<Box sx={{ textAlign: "center", py: 4 }}>
									<Typography variant="h6" color="text.secondary">
										No interceptor data available
									</Typography>
								</Box>
							) : (
								<Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
									{challanComprehensiveStats.top_interceptors.slice(0, 5).map((interceptor, index) => (
										<Box key={interceptor.interceptor_id} className="top-interceptor-card-wrapper">
											<Box className="top-interceptor-card-index-wrapper">{index + 1}</Box>

											<Box sx={{ flex: 1 }}>
												<Typography variant="body1" fontWeight={600}>
													{interceptor.interceptor_numberplate}
												</Typography>

												<Typography variant="body2" color="text.secondary">
													{`${formatNumberMetric(interceptor.challan_count)} challans`}
												</Typography>
											</Box>

											{index === 0 ? <Box className="top-interceptor-card-badge">TOP</Box> : null}
										</Box>
									))}
								</Box>
							)}
						</Box>
					</Grid2>

					{/* Top Jurisdictions Section */}
					<Grid2 size={{ xs: 12, md: 6 }} sx={{ border: "1px solid #e0e0e0", borderRadius: "0.5rem" }}>
						<Box className="dashboard-graph-card">
							<Typography variant="h6" fontWeight={700} mb={1} sx={{ fontSize: 20, letterSpacing: -0.5 }}>
								Top Jurisdictions
							</Typography>

							<Typography variant="body2" color="text.secondary" mb={2} sx={{ fontWeight: 400, fontSize: 14 }}>
								Highest performing RTO jurisdictions by challan count
							</Typography>

							{challanComprehensiveStatsLoading || challanComprehensiveStatsFetching ? (
								<Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
									{Array.from({ length: 5 }).map((_, index) => (
										<Box key={index} sx={{ display: "flex", alignItems: "center", gap: 2 }}>
											<Skeleton variant="circular" width={32} height={32} />
											<Box sx={{ flex: 1 }}>
												<Skeleton variant="text" width="70%" height={20} />
												<Skeleton variant="text" width="50%" height={16} />
											</Box>
											<Skeleton variant="text" width={40} height={20} />
										</Box>
									))}
								</Box>
							) : !challanComprehensiveStats?.top_jurisdictions?.length ? (
								<Box sx={{ textAlign: "center", py: 4 }}>
									<Typography variant="h6" color="text.secondary">
										No jurisdiction data available
									</Typography>
								</Box>
							) : (
								<Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
									{challanComprehensiveStats.top_jurisdictions.slice(0, 5).map((jurisdiction, index) => (
										<Box key={jurisdiction.jurisdiction_id} className="top-jurisdiction-card-wrapper">
											<Box className="top-jurisdiction-card-index-wrapper">{index + 1}</Box>

											<Box sx={{ flex: 1 }}>
												<Typography variant="body1" fontWeight={600}>
													{jurisdiction.jurisdiction_name}
												</Typography>

												<Typography variant="body2" color="text.secondary">
													{`${jurisdiction.jurisdiction_rto_code} • ${formatNumberMetric(jurisdiction.challan_count)} challans`}
												</Typography>
											</Box>

											{index === 0 ? <Box className="top-jurisdiction-card-badge">TOP</Box> : null}
										</Box>
									))}
								</Box>
							)}
						</Box>
					</Grid2>

					{/* Violation Type Statistics Bar Chart */}
					<Grid2 size={{ xs: 12 }} sx={{ border: "1px solid #e0e0e0", borderRadius: "0.5rem" }}>
						<Box className="dashboard-graph-card">
							<Typography variant="h6" fontWeight={700} mb={1} sx={{ fontSize: 20, letterSpacing: -0.5 }}>
								Offence Type Statistics
							</Typography>

							<Typography variant="body2" color="text.secondary" mb={2} sx={{ fontWeight: 400, fontSize: 14 }}>
								Distribution of challans by offence type
							</Typography>

							{getChallanStatsLoading || getChallanStatsFetching ? (
								<Box sx={{ minHeight: "300px", width: "100%", display: "flex", flexDirection: "column", gap: 2 }}>
									<Skeleton variant="rectangular" height={250} />
									<Skeleton variant="text" width="30%" />
									<Skeleton variant="text" width="50%" />
								</Box>
							) : !challanStats?.jurisdiction_statistics?.length ? (
								<Box className="violation-card-no-data-wrapper">
									<Typography variant="h6" color="text.secondary">
										No offence data available
									</Typography>
								</Box>
							) : (
								<ReactECharts
									ref={pdfViolationChartRef}
									option={pdfViolationTypeStatisticsOptions}
									style={{ minHeight: "300px", width: "100%" }}
								/>
							)}
						</Box>
					</Grid2>

					{/* Interceptor Violation Statistics Donut Chart */}
					<Grid2 size={{ xs: 12 }} sx={{ border: "1px solid #e0e0e0", borderRadius: "0.5rem" }}>
						<Box className="dashboard-graph-card">
							<Typography variant="h6" fontWeight={700} mb={1} sx={{ fontSize: 20, letterSpacing: -0.5 }}>
								Interceptor Violation Statistics
							</Typography>

							<Typography variant="body2" color="text.secondary" mb={2} sx={{ fontWeight: 400, fontSize: 14 }}>
								Total challans per interceptor
							</Typography>

							{interceptorChallanStatsLoading || interceptorChallanStatsFetching ? (
								<Box sx={{ position: "relative", flex: 1 }}>
									<Skeleton variant="circular" width={200} height={200} sx={{ mx: "auto" }} />
									<Box sx={{ display: "flex", justifyContent: "center", gap: 2, mt: 2 }}>
										<Skeleton variant="text" width={60} />
										<Skeleton variant="text" width={60} />
										<Skeleton variant="text" width={60} />
									</Box>
								</Box>
							) : !interceptorChallanStats?.interceptor_statistics?.length ? (
								<Box className="interceptor-card-no-data-wrapper">
									<Typography variant="h6" color="text.secondary">
										No interceptor data available
									</Typography>
								</Box>
							) : (
								<>
									<Box sx={{ position: "relative", flex: 1 }}>
										<ReactECharts
											ref={pdfInterceptorChartRef}
											option={pdfInterceptorViolationStatisticsOptions}
											style={{ minHeight: "200px", width: "100%" }}
										/>

										<Box
											sx={{
												position: "absolute",
												top: "50%",
												left: "50%",
												transform: "translate(-50%, -50%)",
												maxWidth: "60%"
											}}
										>
											<Typography fontSize="24px" textAlign="center" color="#00000099" fontWeight={500}>
												{formatNumberMetric(
													interceptorOffenceStatisticsForLegend.reduce((sum, item) => sum + item.total_offences, 0)
												)}
											</Typography>

											<Typography textAlign="center" fontWeight={500} color="#00000099" fontSize="12px">
												Total Offences
											</Typography>
										</Box>
									</Box>

									<Box sx={{ display: "flex", justifyContent: "center", gap: 2, mt: 1, flexWrap: "wrap" }}>
										{interceptorOffenceStatisticsForLegend.slice(0, 4).map((interceptor, index) => {
											const colors = ["#1976D2", "#2196F3", "#03A9F4", "#00BCD4"];
											const totalOffences = interceptorOffenceStatisticsForLegend.reduce(
												(sum, item) => sum + item.total_offences,
												0
											);
											const percentage = totalOffences
												? formatNumberMetric((interceptor.total_offences / totalOffences) * 100, 1)
												: "0";
											const displayName = `${interceptor.interceptor_numberplate} (${interceptor.interceptor_location})`;

											return (
												<Box key={interceptor.interceptor_id} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
													<Box sx={{ width: 12, height: 12, borderRadius: "50%", bgcolor: colors[index] }} />
													{displayName.length > 25 ? (
														<Tooltip arrow placement="top" title={displayName}>
															<Typography variant="body2" sx={{ fontSize: "0.75rem", cursor: "help" }}>
																{`${displayName.substring(0, 25)}...`}: {percentage}%
															</Typography>
														</Tooltip>
													) : (
														<Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
															{displayName}: {percentage}%
														</Typography>
													)}
												</Box>
											);
										})}
										{interceptorOffenceStatisticsForLegend.length > 4 && (
											<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
												<Box sx={{ width: 12, height: 12, borderRadius: "50%", bgcolor: "#9C27B0" }} />

												<Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
													{`+${formatNumberMetric(interceptorOffenceStatisticsForLegend.length - 4)} more`}
												</Typography>
											</Box>
										)}
									</Box>
								</>
							)}
						</Box>
					</Grid2>

					{/* Jurisdiction Violation Statistics Horizontal Bar Chart */}
					{/* <Grid2 size={{ xs: 12 }}>
						<Box className="dashboard-graph-card">
							<Typography variant="h6" fontWeight={700} mb={1} sx={{ fontSize: 20, letterSpacing: -0.5 }}>
								Jurisdiction Violation Statistics
							</Typography>

							<Typography variant="body2" color="text.secondary" mb={2} sx={{ fontWeight: 400, fontSize: 14 }}>
								Violations by RTO jurisdiction
							</Typography>

							{jurisdictionViolationStatisticsLoading || jurisdictionViolationStatisticsFetching ? (
								<Box sx={{ minHeight: "300px", width: "100%", display: "flex", flexDirection: "column", gap: 2 }}>
									<Skeleton variant="rectangular" height={250} />
									<Skeleton variant="text" width="30%" />
									<Skeleton variant="text" width="50%" />
								</Box>
							) : !jurisdictionViolationStatisticsData?.jurisdiction_statistics?.length ? (
								<Box className="jurisdiction-card-no-data-wrapper">
									<Typography variant="h6" color="text.secondary">
										No jurisdiction data available
									</Typography>
								</Box>
							) : (
								<ReactECharts
									ref={pdfJurisdictionChartRef}
									option={pdfJurisdictionViolationStatisticsOptions}
									style={{ minHeight: "300px", width: "100%" }}
								/>
							)}
						</Box>
					</Grid2> */}

					{/* Challan Statistics Stacked Bar Chart */}
					<Grid2 size={{ xs: 12 }} sx={{ border: "1px solid #e0e0e0", borderRadius: "0.5rem" }}>
						<Box className="dashboard-graph-card">
							<Typography variant="h6" fontWeight={700} mb={1} sx={{ fontSize: 20, letterSpacing: -0.5 }}>
								Challan Statistics
							</Typography>

							<Typography variant="body2" color="text.secondary" mb={2} sx={{ fontWeight: 400, fontSize: 14 }}>
								Challan breakdown by offence type and jurisdiction
							</Typography>

							{getChallanStatsLoading || getChallanStatsFetching ? (
								<Box sx={{ minHeight: "300px", width: "100%", display: "flex", flexDirection: "column", gap: 2 }}>
									<Skeleton variant="rectangular" height={250} />
									<Skeleton variant="text" width="30%" />
									<Skeleton variant="text" width="50%" />
								</Box>
							) : !challanStats?.jurisdiction_statistics?.length ? (
								<Box className="jurisdiction-card-no-data-wrapper">
									<Typography variant="h6" color="text.secondary">
										No challan data available
									</Typography>
								</Box>
							) : (
								<>
									<ReactECharts
										ref={pdfChallanStatsChartRef}
										option={pdfChallanStatisticsOptions}
										style={{ minHeight: "400px", width: "100%" }}
									/>

									<Box sx={{ display: "flex", justifyContent: "center", gap: 2, mt: 2, flexWrap: "wrap" }}>
										{offenceStatisticsForLegend.slice(0, 6).map((offence, index) => {
											const colors = [
												"#1976D2",
												"#2196F3",
												"#03A9F4",
												"#00BCD4",
												"#009688",
												"#4CAF50",
												"#8BC34A",
												"#FF9800",
												"#E91E63",
												"#9C27B0",
												"#F44336",
												"#795548",
												"#607D8B"
											];
											const totalChallans = challanStats?.total_challans || 0;
											const percentage = totalChallans
												? formatNumberMetric((offence.challan_count / totalChallans) * 100, 1)
												: "0";

											return (
												<Box key={offence.offence_name} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
													<Box
														sx={{
															width: 12,
															height: 12,
															borderRadius: "2px",
															bgcolor: colors[index % colors.length]
														}}
													/>
													{offence.formattedName.length > 25 ? (
														<Tooltip arrow placement="top" title={offence.formattedName}>
															<Typography variant="body2" sx={{ fontSize: "0.75rem", cursor: "help" }}>
																{`${offence.formattedName.substring(0, 25)}...`}: {percentage}%
															</Typography>
														</Tooltip>
													) : (
														<Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
															{offence.formattedName}: {percentage}%
														</Typography>
													)}
												</Box>
											);
										})}
										{offenceStatisticsForLegend.length > 6 && (
											<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
												<Box
													sx={{
														width: 12,
														height: 12,
														borderRadius: "2px",
														bgcolor: "#9C27B0"
													}}
												/>
												<Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
													{`+${formatNumberMetric(offenceStatisticsForLegend.length - 6)} more`}
												</Typography>
											</Box>
										)}
									</Box>
								</>
							)}
						</Box>
					</Grid2>
				</Grid2>
			</Box>
		</Box>
	);
};

export default ChallanReportsTab;
